import * as React from 'react';
import * as api from 'app/shared/api';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Table, Row, Col, Input, Select, Modal, Button, Space, Tooltip } from 'antd';
import { LockOutlined, PlusOutlined } from '@ant-design/icons';
import { FilterValueType, Product, ProductFilter, ProductKey } from 'app/shared/model/ProductBase';
import {
    filterData,
    getTransferProductById,
    listTransferssProductsData,
    resetTransferModal,
    setFormErrors,
} from 'app/pages/Products/actions';
import { getFilteredData, getTransfersProductsData } from '../selectors';
import DateHelper from 'app/helpers/dateHelper';
import filterHelper from 'app/helpers/filterHelper';
import TableSearch from 'app/shared/components/Table/TableSearch';
import TableActionButtons from 'app/shared/components/Table/TableActionButtons';
import FilterResetLink from 'app/shared/components/Table/FilterResetLink';
import TablePagination from 'app/shared/components/Table/TablePagination';
import { TransfersProduct } from 'app/shared/model';
import AddEditTransferForm from './AddEditTransferForm';
import 'app/shared/components/Table/Table.css';
import { useSignalREvent, PublishedEventType, StatusIcon } from 'app/helpers/signalRHelper';
import { sortProductsWithStatus, useProductGridSorting } from './productGridSorting';
import ProductStatusFilter from './ProductStatusFilter';
import { ProductStatusFilterValue } from './productStatusFilterUtils';
import { buildGridFilteredList } from './productGridFilterPipeline';
import { useAutoOpenProductModal } from '../hooks/useAutoOpenProductModal';

export interface TransfersGridProps {
    
}

interface StateProps {
    filteredData?: Product[] | undefined,
    transfersListData?: TransfersProduct[] | undefined
}

interface DispatchProps {
    onCancel: () => void, 
    onFilterData: (filteredData:Product[] | undefined) => void
    onSelectedTransferData: (transferProduct: TransfersProduct | void) => void
    onPrepareNewTransfer: () => void
}

type InternalProps = StateProps & DispatchProps;

const TransfersGridView = (props:InternalProps) => (
    <>{TransfersGrid(props)}</>
);
import { DivisionId } from 'app/shared/division/getDivisionId';

function TransfersGrid(props:InternalProps) {

    const [showFilters, setShowFilters] = React.useState<boolean>(false);
    const [filters, setFilters] = React.useState<ProductFilter[]>([]);
    const [statusFilter, setStatusFilter] = React.useState<ProductStatusFilterValue>('all');
    const [isSearchActive, setIsSearchActive] = React.useState(false);
    const [searchFilteredData, setSearchFilteredData] = React.useState<TransfersProduct[] | undefined>(undefined);

    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = React.useState<number>(10);
    const { sortKey, sortOrder, getColumnSortProps, handleSorterChange } = useProductGridSorting();

    const productList = props.transfersListData ?? [];

    const publishFilteredData = React.useCallback((
        nextFilters = filters,
        nextShowFilters = showFilters,
        nextStatus = statusFilter,
        nextSearchActive = isSearchActive,
        nextSearchData = searchFilteredData,
    ) => {
        props.onFilterData(buildGridFilteredList(productList, {
            showFilters: nextShowFilters,
            filters: nextFilters,
            isSearchActive: nextSearchActive,
            searchFilteredData: nextSearchData,
            statusFilter: nextStatus,
        }));
    }, [filters, showFilters, statusFilter, isSearchActive, searchFilteredData, productList, props.onFilterData]);

    const [modalOpen, setModalOpen] = React.useState(false);
    const [modalAccId, setModalAccId] = React.useState<number | null>(null);
    const [modalAccName, setModalAccName] = React.useState<string | null>(null);
    const handleSaved = () => {
        setModalOpen(false);
        setModalAccId(null);
        setModalAccName(null);
        setFilters([]);
        setShowFilters(false);
        publishFilteredData([], false, statusFilter, false, undefined);
        setCurrentPage(1);
        props.onCancel();
    };

  const refreshGridEvent = async () => {
    await api.getTransferProducts(DivisionId());
    publishFilteredData(filters, showFilters, statusFilter, isSearchActive, searchFilteredData);
  }

  // Subscribe to event and run refreshGridEvent when triggered
  const { status, lastEventReceived } = useSignalREvent([PublishedEventType.TransferEventPublished], refreshGridEvent, DivisionId());

    // Modal open function
    const getTransferProduct = (transferId: number, name: string, listRecord?: TransfersProduct) => {
        api.getTransferById(DivisionId(), transferId.toString())
            .then(transferProduct => {
                if (!transferProduct) {
                    return;
                }
                if (
                    typeof transferProduct.isUsedInActivePackage !== "boolean" &&
                    typeof listRecord?.isUsedInActivePackage === "boolean"
                ) {
                    transferProduct.isUsedInActivePackage = listRecord.isUsedInActivePackage;
                }
                props.onSelectedTransferData(transferProduct);
                setModalAccId(transferId);
                setModalAccName(name);
                setModalOpen(true);
            });
    };

    useAutoOpenProductModal('Transfers', (id) => getTransferProduct(id, ''));

    const addNewTransfer = () => {
        props.onPrepareNewTransfer();
        setModalAccId(null);
        setModalAccName(null);
        setModalOpen(true);
    };

    // Filtering logic
    const handleFilterChange = (value: any, field: (keyof ProductKey), valueType: FilterValueType = FilterValueType.primaryValue) => {
        let filterList = filterHelper.getFilters(filters, value, field, valueType);
        setFilters(filterList);
        publishFilteredData(filterList);
        setCurrentPage(1);
    };

    const getUniqueTransferTypeFilter = (list: TransfersProduct[]) => {
        const data = list.map(d => ({ id: d.transferTypeText, name: d.transferTypeText }));
        const unique = data.filter((v, i, a) => v.id && a.findIndex(x => x.id === v.id) === i);
        return unique.map(v => ({ label: v.name, value: v.id }));
    };

    const getUniquePortFilter = (list: TransfersProduct[]) => {
        const data = list.map(d => ({ id: d.portText, name: d.portText }));
        const unique = data.filter((v, i, a) => v.id && a.findIndex(x => x.id === v.id) === i);
        return unique.map(v => ({ label: v.name, value: v.id }));
    };

    const columns = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            render: (text: any, record: TransfersProduct) => (
                <>
                    {text}
                    {record.entityUpdate && <LockOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />}
                </>
            )
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            ...getColumnSortProps('name'),
            render: (text: string, record: TransfersProduct) => {
                const nameContent = record.entityUpdate ? (
                    <span style={{ color: 'rgba(0,0,0,0.65)' }}>{text} (Publishing an update)</span>
                ) : (
                    <a href="#0" onClick={e => { e.preventDefault(); getTransferProduct(Number(record.id), record.name, record); }}>
                        {text}
                    </a>
                );

                if (record.entityUpdate) {
                    return (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {nameContent}
                        </span>
                    );
                }
                if (record.isLocked) {
                    return (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Tooltip title="This transfer is linked to an offering. Port and Transfer Type fields are locked and cannot be changed.">
                                <LockOutlined style={{ color: '#9d9d9d', fontSize: 14 }} />
                            </Tooltip>
                            {nameContent}
                        </span>
                    );
                }
                return nameContent;
            }
        },
        {
            title: 'Transfer Type',
            dataIndex: 'transferTypeText',
            key: 'transferTypeText',
            width: 160,
        },
        {
            title: 'Port',
            dataIndex: 'portText',
            key: 'portText',
            width: 130,
        },
        {
            title: 'Last Modified Date',
            dataIndex: 'lastModifiedDate',
            key: 'lastModifiedDate',
            width: 160,
            ...getColumnSortProps('lastModifiedDate'),
            render: (val: Date) => DateHelper.ToDateString(val)
        },
        {
            title: 'Last Updated By',
            dataIndex: 'lastUpdatedBy',
            key: 'lastUpdatedBy',
            width: 150,
            ...getColumnSortProps('lastUpdatedBy'),
        },
        {
            title: 'Active',
            dataIndex: 'active',
            key: 'active',
            width: 100,
            render: (val: boolean) => val ? <span style={{ color: 'green' }}>Yes</span> : <span style={{ color: 'red' }}>No</span>
        }
    ];

    const handleClearFilters = () => {
        setFilters([]);
        setCurrentPage(1);
        publishFilteredData([], showFilters, statusFilter, isSearchActive, searchFilteredData);
    };

    const renderFilterRow = () => {
        if (!showFilters) return null;
        const productList = props.transfersListData ?? [];
        return (
            <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col>
                    <Input
                        placeholder='Search...'
                        value={filterHelper.getFieldValue(filters, "name", "")}
                        onChange={(e: { target: { value: any; }; }) => handleFilterChange(e.target.value, "name")}
                        allowClear
                        style={{ width: 140 }}
                    />
                </Col>
                <Col>
                    <Select
                        mode="multiple"
                        allowClear
                        placeholder="Transfer Type"
                        value={filterHelper.getFieldValue(filters, 'transferTypeText')}
                        onChange={(vals: any) => handleFilterChange(vals, 'transferTypeText')}
                        options={getUniqueTransferTypeFilter(productList)}
                        style={{ width: 160 }}
                    />
                </Col>
                <Col>
                    <Select
                        mode="multiple"
                        allowClear
                        placeholder="Port"
                        value={filterHelper.getFieldValue(filters, 'portText')}
                        onChange={(vals: any) => handleFilterChange(vals, 'portText')}
                        options={getUniquePortFilter(productList)}
                        style={{ width: 140 }}
                    />
                </Col>
                <Col style={{ display: 'flex', alignItems: 'center' }}>
                    <FilterResetLink onClick={handleClearFilters} />
                </Col>
            </Row>
        );
    };

    let data = (props.filteredData ? props.filteredData : productList) as TransfersProduct[];
    const sortedData = React.useMemo(() => {
        return sortProductsWithStatus(data, sortKey, sortOrder, statusFilter);
    }, [data, sortKey, sortOrder, statusFilter]);
    const pagedData = TablePagination.dataPaging(sortedData, currentPage, itemsPerPage);
    const totalItems = sortedData.length;

    return (
        <div>
            <Row gutter={16} style={{ marginBottom: 12 }}>
                <Col flex={2}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                            <TableSearch
                                disabled={showFilters}
                                dataList={productList}
                                onSearch={(data: Product[], searchActive = false) => {
                                    setCurrentPage(1);
                                    setIsSearchActive(searchActive);
                                    setSearchFilteredData(data as TransfersProduct[]);
                                    publishFilteredData(filters, showFilters, statusFilter, searchActive, data as TransfersProduct[]);
                                }}
                            />
                        </div>
                        <ProductStatusFilter
                            value={statusFilter}
                            onChange={(nextStatus) => {
                                setStatusFilter(nextStatus);
                                setCurrentPage(1);
                                publishFilteredData(filters, showFilters, nextStatus, isSearchActive, searchFilteredData);
                            }}
                        />
                    </div>
                </Col>
                <Col flex={3} style={{ textAlign: 'right' }}>
                    <Space>
                        <StatusIcon status={status} lastEventReceived={lastEventReceived} unpublishedChanges={Array.isArray(data) && data.some(x => x?.entityUpdate === true)} onManualRefresh={refreshGridEvent}/>
                        <TableActionButtons
                            showFilters={showFilters}
                            dataList={props.filteredData ? props.filteredData : (props.transfersListData ? props.transfersListData : [])}
                            onShowFiltersClick={() => {
                                const nextShowFilters = !showFilters;
                                setShowFilters(nextShowFilters);
                                if (nextShowFilters) {
                                    setFilters([]);
                                    publishFilteredData([], true, statusFilter, isSearchActive, searchFilteredData);
                                } else {
                                    publishFilteredData(filters, false, statusFilter, isSearchActive, searchFilteredData);
                                }
                                setCurrentPage(1);
                            }}
                            canFilter canExport canEdit={false} canAdd={false}
                            csvFileName={`${DateHelper.ToDateString(new Date(), 'yyyy.MM.dd')}_Transfers_Export`}
                            />
                    </Space>
                </Col>
                <Col>
                        <Button type="primary" onClick={() => { addNewTransfer(); setModalOpen(true); }} icon={<PlusOutlined />}>
                            Add a new Transfer
                        </Button>
                </Col>
         
            </Row>
            {renderFilterRow()}
            <Table
                columns={columns}
                dataSource={pagedData}
                rowKey="id"
                pagination={false}
                bordered
                size="middle"
                className='main row'
                style={{ marginBottom: 12 }}
                showSorterTooltip={false}
                onChange={(_, __, sorter: any, extra: any) => {
                    handleSorterChange(sorter, extra?.action);
                }}
            />
            <TablePagination.Paging 
                noOfItems={totalItems}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onItemsPerPageChange={(value: number, currentPage: number) => { setItemsPerPage(value); setCurrentPage(currentPage); }}
                onPageChange={(value: number) => { setCurrentPage(value); }}
                showItemsPerPage
            />
            <Modal
                title={modalAccId ? "Update Transfer Product" : "Add Transfer Product"}
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    setModalAccId(null);
                    setModalAccName(null);
                    props.onCancel();
                }}
                footer={null}
                width={980}
                style={{ top: 50 }}
                destroyOnHidden
            >
                <AddEditTransferForm key={modalAccId ?? 'new'} onSuccess={handleSaved} />
            </Modal>
        </div>
    );
}

const mapStateToProps = (state: any, ownProps: TransfersGridProps): StateProps => ({
    filteredData: getFilteredData(state),
    transfersListData: getTransfersProductsData(state)
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
    onFilterData: (filteredData?: Product[]) => {
        dispatch(filterData(filteredData as any));
    },
    onSelectedTransferData: (transferProduct: TransfersProduct) => {
        dispatch(getTransferProductById(transferProduct))
    },
      onPrepareNewTransfer: () => {
        dispatch(setFormErrors([]));
        dispatch(resetTransferModal());
    },
    onCancel: () => {
        dispatch(setFormErrors([]));
        dispatch(resetTransferModal());
        // api.getTransferProducts(DivisionId())
        //     .then(transferProducts => {
        //         dispatch(resetTransferModal());
        //         dispatch(listTransferssProductsData(transferProducts))
        //     });
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(TransfersGridView);